<!-- YouTubeTennisStatsOverlay.vue - Compact top overlay -->
<template>
    <div class="youtube-tennis-overlay" :class="{ 'active': visible, 'mobile': isMobile }">

      <!-- Single row layout with all info -->
      <div class="overlay-content">

        <!-- Tournament info -->
        <div class="tournament-section" v-if="matchData">
          <div class="tournament-badge">
            <span class="tournament-text">{{ matchData.round }}</span>
            <div class="status-indicator" :class="matchData.status.toLowerCase()">
              {{ matchData.status }}
            </div>
          </div>
          <div class="court-info" v-if="matchData.court">{{ matchData.court }}</div>
        </div>

        <!-- Players and scores -->
        <div class="players-section" v-if="matchData && matchData.players">
          <div class="player-block"
               v-for="(player, index) in matchData.players"
               :key="index"
               :class="{ 'winner': player.isWinner, 'serving': player.isServing }">

            <div class="player-info">
              <div class="player-name">
                <div class="serve-dot" v-if="player.isServing"></div>
                <span class="name">{{ getDisplayName(player) }}</span>
                <span class="ranking" v-if="player.ranking">#{{ player.ranking }}</span>
              </div>
            </div>

            <div class="sets-display">
              <div class="set-score"
                   v-for="(setScore, setIndex) in player.sets"
                   :key="setIndex"
                   :class="{
                     'won': setScore > (matchData.players[1-index].sets[setIndex] || 0),
                     'current': setIndex === currentSet
                   }">
                {{ setScore }}
              </div>
            </div>
          </div>
        </div>

        <!-- Compact stats with rotation -->
        <div class="stats-section" v-if="statsData">
          <div class="stat-group"
               v-for="(stat, index) in currentDisplayStats"
               :key="stat.key"
               :style="{ animationDelay: (index * 0.1) + 's' }">
            <div class="stat-label">{{ stat.label }}</div>
            <div class="stat-values">
              <span class="stat-value" :class="{ 'leading': stat.player1 > stat.player2 }">
                {{ formatStatValue(stat.player1, stat.type, stat.total1) }}
              </span>
              <span class="stat-separator">-</span>
              <span class="stat-value" :class="{ 'leading': stat.player2 > stat.player1 }">
                {{ formatStatValue(stat.player2, stat.type, stat.total2) }}
              </span>
            </div>
            <div class="stat-bar" v-if="stat.showBar">
              <div class="bar-fill p1" :style="{ width: getBarWidth(stat.player1, stat.player2, 'player1') }"></div>
              <div class="bar-fill p2" :style="{ width: getBarWidth(stat.player1, stat.player2, 'player2') }"></div>
            </div>
          </div>
        </div>

        <!-- Momentum indicator -->
        <div class="momentum-section" v-if="momentumData">
          <div class="momentum-label">Momentum</div>
          <div class="momentum-bar">
            <div class="momentum-fill"
                 :style="{
                   width: Math.abs(momentumData.value) + '%',
                   left: momentumData.value > 0 ? '50%' : (50 - Math.abs(momentumData.value)) + '%'
                 }"
                 :class="momentumData.value > 0 ? 'player2' : 'player1'">
            </div>
            <div class="momentum-center"></div>
          </div>
        </div>

      </div>
    </div>
  </template>

  <script>
  export default {
    name: 'YouTubeTennisStatsOverlay',
    props: {
      visible: {
        type: Boolean,
        default: true
      },
      isMobile: {
        type: Boolean,
        default: false
      },
      matchData: {
        type: Object,
        default: () => null
      },
      statsData: {
        type: Object,
        default: () => null
      },
      momentumData: {
        type: Object,
        default: () => null
      }
    },
    data() {
      return {
        currentSet: 2,
        currentStatIndex: 0,
        statRotationInterval: null
      }
    },
    computed: {
      allStats() {
        if (!this.statsData) return [];
        return [
          {
            key: 'aces',
            label: 'Aces',
            player1: this.statsData.aces ? this.statsData.aces[0] : 0,
            player2: this.statsData.aces ? this.statsData.aces[1] : 0,
            type: 'number',
            showBar: true
          },
          {
            key: 'firstServe',
            label: '1st Serve %',
            player1: this.statsData.firstServePercent ? this.statsData.firstServePercent[0] : 0,
            player2: this.statsData.firstServePercent ? this.statsData.firstServePercent[1] : 0,
            type: 'percentage',
            showBar: true
          },
          {
            key: 'firstServeWin',
            label: '1st Serve Win %',
            player1: this.statsData.firstServeWinPercent ? this.statsData.firstServeWinPercent[0] : 0,
            player2: this.statsData.firstServeWinPercent ? this.statsData.firstServeWinPercent[1] : 0,
            type: 'percentage',
            showBar: true
          },
          {
            key: 'breakPoints',
            label: 'Break Points',
            player1: this.statsData.breakPointsConverted ? this.statsData.breakPointsConverted[0] : 0,
            player2: this.statsData.breakPointsConverted ? this.statsData.breakPointsConverted[1] : 0,
            total1: this.statsData.breakPointsTotal ? this.statsData.breakPointsTotal[0] : 0,
            total2: this.statsData.breakPointsTotal ? this.statsData.breakPointsTotal[1] : 0,
            type: 'fraction',
            showBar: false
          },
          {
            key: 'points',
            label: 'Total Points',
            player1: this.statsData.totalPointsWon ? this.statsData.totalPointsWon[0] : 0,
            player2: this.statsData.totalPointsWon ? this.statsData.totalPointsWon[1] : 0,
            type: 'number',
            showBar: true
          }
        ];
      },
      currentDisplayStats() {
        return this.allStats.slice(this.currentStatIndex, this.currentStatIndex + 3);
      }
    },
    methods: {
      getDisplayName(player) {
        return `${player.firstName} ${player.lastName}`;
      },
      formatStatValue(value, type, total = null) {
        if (type === 'percentage') return value + '%';
        if (type === 'fraction' && total !== null) return `${value}/${total}`;
        return value.toString();
      },
      getBarWidth(value1, value2, player) {
        const total = value1 + value2;
        if (total === 0) return '0%';
        const percentage = player === 'player1' ? (value1 / total) * 100 : (value2 / total) * 100;
        return Math.min(percentage, 100) + '%';
      },
      startStatRotation() {
        if (this.allStats.length <= 3) return;

        this.statRotationInterval = setInterval(() => {
          this.currentStatIndex = (this.currentStatIndex + 1) % (this.allStats.length - 2);
        }, 4000); // Rotate every 4 seconds
      },
      stopStatRotation() {
        if (this.statRotationInterval) {
          clearInterval(this.statRotationInterval);
          this.statRotationInterval = null;
        }
      }
    },
    mounted() {
      this.startStatRotation();
    },
    beforeUnmount() {
      this.stopStatRotation();
    },
  };
  </script>

  <style scoped>
  .youtube-tennis-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: auto;
    max-height: 15vh;
    font-family: 'Inter', 'SF Pro Display', system-ui, sans-serif;
    background: linear-gradient(180deg,
      rgba(0, 8, 25, 0.95) 0%,
      rgba(0, 8, 25, 0.85) 100%);
    border-bottom: 1px solid rgba(0, 245, 255, 0.3);
    backdrop-filter: blur(20px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    color: white;
    opacity: 0;
    transform: translateY(-100%);
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1000;
    overflow: hidden;
  }

  .youtube-tennis-overlay.active {
    opacity: 1;
    transform: translateY(0);
  }

  .youtube-tennis-overlay::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(0, 245, 255, 0.6) 50%,
      transparent 100%);
    animation: bottomGlow 3s ease-in-out infinite;
  }

  @keyframes bottomGlow {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }

  .overlay-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 20px;
    gap: 20px;
    min-height: 50px;
  }

  /* Tournament Section */
  .tournament-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    flex-shrink: 0;
  }

  .tournament-badge {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tournament-text {
    font-size: 10px;
    font-weight: 600;
    color: #00f5ff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .status-indicator {
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 8px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .status-indicator.live {
    background: linear-gradient(135deg, #ff4444, #ff6b6b);
    color: white;
    animation: livePulse 2s ease-in-out infinite;
  }

  .status-indicator.final {
    background: linear-gradient(135deg, #10b981, #34d399);
    color: white;
  }

  @keyframes livePulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 68, 68, 0.7); }
    50% { box-shadow: 0 0 0 3px rgba(255, 68, 68, 0); }
  }

  .court-info {
    font-size: 8px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
  }

  /* Players Section */
  .players-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    max-width: 300px;
  }

  .player-block {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.3s ease;
    position: relative;
  }

  .player-block.winner {
    background: rgba(0, 245, 255, 0.1);
  }

  .player-block.serving::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 70%;
    background: linear-gradient(180deg, #00f5ff, #00ffcc);
    border-radius: 1px;
    animation: servePulse 1.5s ease-in-out infinite;
  }

  @keyframes servePulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }

  .player-info {
    flex: 1;
  }

  .player-name {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .serve-dot {
    width: 4px;
    height: 4px;
    background: #00f5ff;
    border-radius: 50%;
    box-shadow: 0 0 6px #00f5ff;
    animation: serveDotPulse 1s ease-in-out infinite;
  }

  @keyframes serveDotPulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.3); opacity: 0.7; }
  }

  .name {
    font-size: 12px;
    font-weight: 600;
    color: white;
    letter-spacing: -0.2px;
  }

  .player-block.winner .name {
    color: #00f5ff;
  }

  .ranking {
    font-size: 9px;
    color: #ff6b9d;
    font-weight: 600;
  }

  .sets-display {
    display: flex;
    gap: 3px;
    align-items: center;
  }

  .set-score {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    font-size: 11px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.8);
    transition: all 0.3s ease;
  }

  .set-score.won {
    background: rgba(0, 245, 255, 0.2);
    border-color: #00f5ff;
    color: #00f5ff;
    box-shadow: 0 0 6px rgba(0, 245, 255, 0.3);
  }

  .set-score.current {
    background: rgba(255, 215, 0, 0.2);
    border-color: #ffd700;
    color: #ffd700;
    box-shadow: 0 0 8px rgba(255, 215, 0, 0.4);
  }

  /* Stats Section */
  .stats-section {
    display: flex;
    gap: 16px;
    align-items: center;
    flex-shrink: 0;
  }

  .stat-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    position: relative;
  }

  .stat-label {
    font-size: 8px;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-values {
    display: flex;
    align-items: center;
    gap: 3px;
  }

  .stat-value {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 4px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.9);
    transition: all 0.3s ease;
    min-width: 18px;
    text-align: center;
  }

  .stat-value.leading {
    background: rgba(0, 245, 255, 0.2);
    color: #00f5ff;
    box-shadow: 0 0 6px rgba(0, 245, 255, 0.3);
  }

  .stat-separator {
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
  }

  .stat-bar {
    display: flex;
    height: 2px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 1px;
    overflow: hidden;
    width: 40px;
    margin-top: 2px;
  }

  .bar-fill {
    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .bar-fill.p1 {
    background: linear-gradient(90deg, #ff6b9d, #ff8fab);
  }

  .bar-fill.p2 {
    background: linear-gradient(90deg, #00f5ff, #00d4aa);
  }

  /* Momentum Section */
  .momentum-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    width: 80px;
  }

  .momentum-label {
    font-size: 8px;
    color: rgba(255, 255, 255, 0.6);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
  }

  .momentum-bar {
    width: 100%;
    height: 3px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    position: relative;
    overflow: hidden;
  }

  .momentum-fill {
    height: 100%;
    position: absolute;
    border-radius: 2px;
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .momentum-fill.player1 {
    background: linear-gradient(90deg, #ff6b9d, #ff8fab);
  }

  .momentum-fill.player2 {
    background: linear-gradient(90deg, #00f5ff, #00d4aa);
  }

  .momentum-center {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.4);
    transform: translateX(-50%);
  }

  /* Mobile Responsive */
  .mobile .overlay-content {
    padding: 6px 15px;
    gap: 12px;
  }

  .mobile .stats-section {
    gap: 12px;
  }

  .mobile .momentum-section {
    width: 60px;
  }

  .mobile .players-section {
    max-width: 250px;
  }

  @media (max-width: 768px) {
    .overlay-content {
      padding: 6px 15px;
      gap: 12px;
    }

    .stats-section {
      gap: 10px;
    }

    .momentum-section {
      width: 60px;
    }

    .players-section {
      max-width: 200px;
    }
  }

  @media (max-width: 480px) {
    .overlay-content {
      padding: 5px 10px;
      gap: 8px;
    }

    .stats-section {
      display: none; /* Hide stats on very small screens to save space */
    }

    .players-section {
      max-width: 180px;
    }

    .momentum-section {
      width: 50px;
    }
  }

  /* Entrance animation */
  .stat-group {
    animation: statSlideIn 0.6s ease-out;
    animation-fill-mode: both;
  }

  .stat-group:nth-child(1) { animation-delay: 0.1s; }
  .stat-group:nth-child(2) { animation-delay: 0.2s; }
  .stat-group:nth-child(3) { animation-delay: 0.3s; }

  @keyframes statSlideIn {
    0% {
      opacity: 0;
      transform: translateY(-10px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  </style>
